import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import CourseCard from '../../course-catalog/components/CourseCard';
import { listCatalogListings } from '../../../utils/catalogHelpers';

// Shown only while there are zero live published courses, so the homepage
// never looks empty before the catalog has real content in it.
const fallbackCourses = [
  {
    id: 'mock-1',
    title: 'Complete Machine Learning Bootcamp',
    description: 'Master machine learning from basics to advanced concepts with hands-on projects and real-world applications.',
    category: 'Artificial Intelligence',
    price: 199.99,
    isFree: false,
    providerName: 'Nesolearn Academy',
    providerVerified: true,
    enrollmentType: 'open',
    moduleCount: 8,
    lessonCount: 42,
    thumbnail: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400&h=225&fit=crop',
    isLive: false,
  },
  {
    id: 'mock-2',
    title: 'AI Ethics and Responsible Development',
    description: 'Learn about ethical considerations in AI development, bias mitigation, and responsible AI practices.',
    category: 'Artificial Intelligence',
    price: 0,
    isFree: true,
    providerName: 'Nesolearn Academy',
    providerVerified: true,
    enrollmentType: 'open',
    moduleCount: 4,
    lessonCount: 16,
    thumbnail: 'https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?w=400&h=225&fit=crop',
    isLive: false,
  },
  {
    id: 'mock-3',
    title: 'Blockchain Compliance Fundamentals',
    description: 'Understand regulatory frameworks, AML compliance, and transaction monitoring in blockchain ecosystems.',
    category: 'Blockchain & Web3',
    price: 149.99,
    isFree: false,
    providerName: 'Africa Web3 Institute',
    providerVerified: true,
    enrollmentType: 'cohort',
    moduleCount: 6,
    lessonCount: 24,
    thumbnail: 'https://images.pixabay.com/photo/2018/05/08/08/44/artificial-intelligence-3382507_1280.jpg?w=400&h=225&fit=crop',
    isLive: false,
  },
  {
    id: 'mock-4',
    title: 'Data Science for AI Applications',
    description: 'Learn data preprocessing, analysis, and visualization techniques essential for AI projects.',
    category: 'Data Science',
    price: 99.99,
    isFree: false,
    providerName: 'Nesolearn Academy',
    providerVerified: true,
    enrollmentType: 'open',
    moduleCount: 5,
    lessonCount: 20,
    thumbnail: 'https://images.pexels.com/photos/590022/pexels-photo-590022.jpeg?w=400&h=225&fit=crop',
    isLive: false,
  },
];

const normalizeLive = (listing) => ({
  id: listing?.courseId,
  courseId: listing?.courseId,
  orgId: listing?.providerId,
  title: listing?.title,
  description: listing?.description,
  category: listing?.category || 'Other',
  price: listing?.priceNesol || 0,
  isFree: listing?.isFree !== false,
  providerName: listing?.providerName || 'Unknown Provider',
  providerVerified: !!listing?.providerVerified,
  enrollmentType: listing?.enrollmentType || 'invite',
  moduleCount: listing?.moduleCount || 0,
  lessonCount: listing?.lessonCount || 0,
  thumbnail: listing?.thumbnailUrl || '',
  isLive: true,
});

const CoursePreview = () => {
  const [liveCourses, setLiveCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadLive = async () => {
      setIsLoading(true);
      const { listings } = await listCatalogListings();
      setLiveCourses((listings || [])?.map(normalizeLive));
      setIsLoading(false);
    };
    loadLive();
  }, []);

  const usingLiveData = liveCourses?.length > 0;
  const statsSource = usingLiveData ? liveCourses : fallbackCourses;
  const displayCourses = statsSource?.slice(0, 4);

  // Every number here comes straight from catalogListings, which is publicly
  // readable per the Firestore rules — no per-user enrollment data involved.
  const stats = useMemo(() => {
    const totalCourses = statsSource?.length || 0;
    const totalLessons = statsSource?.reduce((sum, c) => sum + (c?.lessonCount || 0), 0);

    const providerKeys = new Set(
      statsSource
        ?.filter((c) => c?.providerVerified)
        ?.map((c) => c?.orgId || c?.providerName)
    );

    return {
      totalCourses,
      totalLessons,
      verifiedProviders: providerKeys.size,
    };
  }, [statsSource]);

  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Courses building a verifiable track record
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-2">
            Every course below writes directly to your Nesolearn ID. Modules, lessons, and outcomes an institution or employer can check, not just take your word for.
          </p>
          {!usingLiveData && !isLoading && (
            <p className="text-xs text-warning">
              These are sample courses. Live courses from verified institutions replace them as providers publish.
            </p>
          )}
        </div>

        {/* Courses Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                        {Array.from({ length: 4 })?.map((_, index) => (
              <div key={index} className="aspect-[3/4] rounded-3xl bg-white/5 border border-white/10 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {displayCourses?.map((course) => (
              <CourseCard key={course?.id} course={course} />
            ))}
          </div>
        )}

        {/* View All Courses CTA */}
        <div className="text-center">
          <Link to="/course-catalog">
            <Button
              variant="outline"
              size="lg"
              iconName="ArrowRight"
              iconPosition="right"
              className="min-w-[200px]"
            >
              View All Courses
            </Button>
          </Link>
        </div>

        {/* Platform Stats — real counts from the public course catalog */}
        {!isLoading && (
          <div className="mt-16 bg-muted rounded-2xl p-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold text-foreground mb-2">{stats.totalCourses}</div>
                <div className="text-sm text-muted-foreground">
                  {usingLiveData ? 'Courses' : 'Sample Courses'}
                </div>
              </div>
              <div>
                <div className="text-3xl font-bold text-foreground mb-2">{stats.totalLessons}</div>
                <div className="text-sm text-muted-foreground">Lessons</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-foreground mb-2">{stats.verifiedProviders}</div>
                <div className="text-sm text-muted-foreground">Verified Providers</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default CoursePreview;